import React from "react";

// Renders text that may contain markdown-style links: [text](url)
function RichText({ text, style = {} }) {
  if (!text) return null;
  const parts = [];
  const regex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#009dc4", textDecoration: "underline", fontWeight: 600 }}
      >
        {match[1]}
      </a>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <span style={style}>{parts}</span>;
}

export default function LessonContent({ blocks = [] }) {
  return (
    <div style={{ lineHeight: 1.8, fontFamily: "'Nunito', 'Segoe UI', Arial, sans-serif" }}>
      {blocks.map((block, i) => {

        if (block.type === "paragraph")
          return (
            <p key={i} style={{ color: "#374151", marginBottom: 14, lineHeight: 1.85, fontSize: 14 }}>
              <RichText text={block.text} />
            </p>
          );

        if (block.type === "hero")
          return (
            <p key={i} style={{
              fontSize: 15, color: "#1e293b", marginBottom: 18, lineHeight: 1.9,
              fontWeight: 500, borderLeft: "3px solid #009dc4", paddingLeft: 16,
              background: "#f0f9ff", padding: "12px 16px", borderRadius: "0 6px 6px 0",
            }}>
              <RichText text={block.text} />
            </p>
          );

        if (block.type === "heading")
          return (
            <h3 key={i} style={{
              fontWeight: 800, color: "#1e293b", marginTop: 28, marginBottom: 10,
              fontSize: 16, borderBottom: "2px solid #f0f9ff", paddingBottom: 6,
            }}>
              {block.text}
            </h3>
          );

        if (block.type === "list")
          return (
            <ul key={i} style={{ marginBottom: 16, paddingLeft: 0, listStyle: "none" }}>
              {block.items.map((item, j) => (
                <li key={j} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
                  <span style={{ color: "#009dc4", fontWeight: 700, flexShrink: 0, marginTop: 3, fontSize: 16 }}>·</span>
                  <span style={{ color: "#374151", fontSize: 14, lineHeight: 1.75 }}>
                    <RichText text={item} />
                  </span>
                </li>
              ))}
            </ul>
          );

        if (block.type === "callout") {
          const themes = {
            info:    { bg: "#eff6ff", border: "#bfdbfe", title: "#1d4ed8", body: "#1e3a8a" },
            success: { bg: "#f0fdf4", border: "#bbf7d0", title: "#16a34a", body: "#14532d" },
            warning: { bg: "#fffbeb", border: "#fde68a", title: "#d97706", body: "#78350f" },
            danger:  { bg: "#fef2f2", border: "#fecaca", title: "#dc2626", body: "#7f1d1d" },
          };
          const t = themes[block.variant] || themes.info;
          return (
            <div key={i} style={{
              background: t.bg, border: `1px solid ${t.border}`,
              borderRadius: 8, padding: "14px 16px", marginBottom: 16,
            }}>
              {block.title && (
                <p style={{ fontWeight: 800, color: t.title, marginBottom: 4, fontSize: 13 }}>
                  {block.title}
                </p>
              )}
              <p style={{ color: t.body, fontSize: 13, lineHeight: 1.8, margin: 0 }}>
                <RichText text={block.text} />
              </p>
            </div>
          );
        }

        if (block.type === "stat")
          return (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: 10, marginBottom: 18,
            }}>
              {block.stats.map((s, j) => (
                <div key={j} style={{
                  background: "#f0f9ff", border: "1px solid #bae6fd",
                  borderRadius: 8, padding: "14px 10px", textAlign: "center",
                }}>
                  <div style={{ fontWeight: 900, color: "#009dc4", fontSize: 22 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 3, lineHeight: 1.4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          );

        if (block.type === "myth")
          return (
            <div key={i} style={{ marginBottom: 16 }}>
              {block.myths.map((m, j) => (
                <div key={j} style={{ marginBottom: 8, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ background: "#fef2f2", padding: "10px 14px", display: "flex", gap: 8 }}>
                    <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>✗ Myth:</span>
                    <span style={{ color: "#7f1d1d", fontSize: 13, lineHeight: 1.6 }}>{m.myth}</span>
                  </div>
                  <div style={{ background: "#f0fdf4", padding: "10px 14px", display: "flex", gap: 8 }}>
                    <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>✓ Reality:</span>
                    <span style={{ color: "#14532d", fontSize: 13, lineHeight: 1.6 }}>{m.reality}</span>
                  </div>
                </div>
              ))}
            </div>
          );

        if (block.type === "table")
          return (
            <div key={i} style={{ marginBottom: 20, overflowX: "auto" }}>
              {block.title && (
                <p style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, marginBottom: 8 }}>
                  {block.title}
                </p>
              )}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                {block.headers && (
                  <thead>
                    <tr style={{ background: "#f0f9ff" }}>
                      {block.headers.map((h, j) => (
                        <th key={j} style={{
                          padding: "8px 12px", textAlign: "left", fontWeight: 700,
                          color: "#1e293b", borderBottom: "2px solid #009dc4",
                          fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5,
                          whiteSpace: "nowrap",
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {block.rows.map((row, j) => (
                    <tr key={j} style={{ background: j % 2 === 0 ? "#fff" : "#fafbfc" }}>
                      {row.map((cell, k) => (
                        <td key={k} style={{
                          padding: "8px 12px", color: "#374151",
                          borderBottom: "1px solid #f1f5f9", lineHeight: 1.6,
                          verticalAlign: "top",
                        }}>
                          <RichText text={cell} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

        if (block.type === "earnings")
          return (
            <div key={i} style={{ marginBottom: 18 }}>
              {block.title && (
                <p style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, marginBottom: 8 }}>
                  {block.title}
                </p>
              )}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                  background: "#f8fafc", padding: "8px 12px", borderBottom: "1px solid #e2e8f0",
                }}>
                  {["Level", "Pay range", "Notes"].map((h, j) => (
                    <span key={j} style={{
                      fontSize: 11, fontWeight: 700, color: "#64748b",
                      textTransform: "uppercase", letterSpacing: 0.5,
                    }}>{h}</span>
                  ))}
                </div>
                {block.rows.map((row, j) => (
                  <div key={j} style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                    padding: "9px 12px",
                    borderBottom: j < block.rows.length - 1 ? "1px solid #f1f5f9" : "none",
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{row.level}</span>
                    <span style={{ fontSize: 12, color: "#009dc4", fontWeight: 700 }}>{row.pay}</span>
                    <span style={{ fontSize: 12, color: "#64748b" }}>{row.note}</span>
                  </div>
                ))}
              </div>
            </div>
          );

        if (block.type === "timeline")
          return (
            <div key={i} style={{ marginBottom: 18 }}>
              {block.title && (
                <p style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, marginBottom: 10 }}>
                  {block.title}
                </p>
              )}
              {block.steps.map((step, j) => (
                <div key={j} style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: "50%",
                    background: j === 0 ? "#009dc4" : "#e2e8f0",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: j === 0 ? "#fff" : "#94a3b8" }}>
                      {j + 1}
                    </span>
                  </div>
                  <div style={{
                    flex: 1, paddingBottom: 12,
                    borderBottom: j < block.steps.length - 1 ? "1px dashed #e2e8f0" : "none",
                  }}>
                    <p style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, margin: "0 0 2px" }}>
                      {step.label}
                    </p>
                    <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                      <RichText text={step.detail} />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          );

        if (block.type === "platforms")
          return (
            <div key={i} style={{ marginBottom: 16 }}>
              {block.title && (
                <p style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, marginBottom: 8 }}>
                  {block.title}
                </p>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {block.platforms.map((p, j) => (
                  <span key={j} style={{
                    background: "#f0f9ff", color: "#0369a1",
                    border: "1px solid #bae6fd", borderRadius: 4,
                    fontSize: 12, fontWeight: 600, padding: "3px 10px",
                  }}>
                    {p}
                  </span>
                ))}
              </div>
            </div>
          );

        if (block.type === "quote")
          return (
            <div key={i} style={{
              borderLeft: "3px solid #009dc4", paddingLeft: 16, marginBottom: 16,
              background: "#f8fafc", padding: "12px 16px", borderRadius: "0 6px 6px 0",
            }}>
              <p style={{ color: "#374151", fontSize: 15, lineHeight: 1.85, fontStyle: "italic", margin: 0 }}>
                "<RichText text={block.text} />"
              </p>
              {block.author && (
                <p style={{ color: "#64748b", fontSize: 12, fontWeight: 600, margin: "6px 0 0" }}>
                  — {block.author}
                </p>
              )}
            </div>
          );

        if (block.type === "video")
          return (
            <div key={i} style={{ marginBottom: 16 }}>
              <a
                href={block.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: "#f8fafc", border: "1px solid #e2e8f0",
                  borderRadius: 8, padding: "12px 14px", textDecoration: "none",
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#009dc4"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "#e2e8f0"}
              >
                <span style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: "#ff0000", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}>
                  <span style={{ color: "#fff", fontSize: 14, marginLeft: 2 }}>▶</span>
                </span>
                <div>
                  <p style={{ color: "#1e293b", fontWeight: 700, fontSize: 13, margin: 0 }}>
                    {block.title}
                  </p>
                  {block.channel && (
                    <p style={{ color: "#64748b", fontSize: 12, margin: "2px 0 0" }}>
                      {block.channel}
                    </p>
                  )}
                  {block.description && (
                    <p style={{ color: "#64748b", fontSize: 12, margin: "2px 0 0", lineHeight: 1.5 }}>
                      {block.description}
                    </p>
                  )}
                </div>
                <span style={{ marginLeft: "auto", color: "#009dc4", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                  Watch →
                </span>
              </a>
            </div>
          );

        if (block.type === "resource_group")
          return (
            <div key={i} style={{ marginBottom: 20 }}>
              {block.title && (
                <p style={{
                  fontWeight: 800, color: "#009dc4", fontSize: 12,
                  textTransform: "uppercase", letterSpacing: 1, marginBottom: 8,
                }}>
                  {block.title}
                </p>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {block.resources.map((r, j) => (
                  <a
                    key={j}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 10,
                      background: "#fafbfc", border: "1px solid #e2e8f0",
                      borderRadius: 6, padding: "10px 12px", textDecoration: "none",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#009dc4"; e.currentTarget.style.background = "#f0f9ff"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fafbfc"; }}
                  >
                    <span style={{
                      background: r.free !== false ? "#dcfce7" : "#fef9c3",
                      color: r.free !== false ? "#16a34a" : "#a16207",
                      fontSize: 10, fontWeight: 700, padding: "2px 6px",
                      borderRadius: 3, flexShrink: 0, marginTop: 1, whiteSpace: "nowrap",
                    }}>
                      {r.free !== false ? "FREE" : "PAID"}
                    </span>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: "#1e293b", fontWeight: 700, fontSize: 13, margin: 0 }}>
                        {r.title}
                      </p>
                      {r.description && (
                        <p style={{ color: "#64748b", fontSize: 12, margin: "2px 0 0", lineHeight: 1.5 }}>
                          {r.description}
                        </p>
                      )}
                    </div>
                    <span style={{ color: "#009dc4", fontSize: 12, fontWeight: 600, flexShrink: 0, marginTop: 1 }}>
                      Visit →
                    </span>
                  </a>
                ))}
              </div>
            </div>
          );

        return null;
      })}
    </div>
  );
}